import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import CrownImg from '@src/assets/images/crown.png';
import route from '@src/constants/route';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import {
  checkCustomPackage,
  getDefaultValueCustomResource,
} from '@src/services/package';
import usePaywallTracking from '@src/hooks/usePaywallTracking';
import {
  MOE_MODULE,
  MOE_PAYWALL_CTA,
  MOE_PAYWALL_TYPE,
} from '@src/constants/moengage';

const BUTTON_40_STYLE = {
  padding: '8px 24px',
  borderRadius: '8px',
  height: 40,
};

const PopupNotEnoughCredits = ({ open, onClose, projectId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { usingPackage, latestOrder } = useSelector((state) => state.user);
  const { handleGetTopUpPackageCode, handleGetPackageCodeMapping } =
    useMappingPackage();

  const isFreePackage = FREE_PACKAGE_CODES.includes(usingPackage?.code);
  const isCustomPackage = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage?.level,
  });

  const { packageExpiryDate } = latestOrder || {};
  const hasExpired =
    packageExpiryDate && moment(packageExpiryDate).isBefore(moment());
  const showBuyMoreCredits = !isFreePackage && !isCustomPackage && !hasExpired;

  const {
    defaultCredits = 0,
    defaultDevices = 0,
    defaultVoiceClonings = 0,
    concurrentRequestApi = 0,
  } = getDefaultValueCustomResource(usingPackage?.customResource) || {};

  // Hit tracked on open — translate, rewrite and video export all share this popup
  const { trackCta } = usePaywallTracking({
    paywallType: MOE_PAYWALL_TYPE.NOT_ENOUGH_CREDITS,
    module: MOE_MODULE.PROJECT,
    projectId,
    open,
  });

  const handleBuyMoreCredits = () => {
    onClose();
    const topUpPackageCode = handleGetTopUpPackageCode({
      type: usingPackage?.type,
      level: usingPackage?.level,
    });
    trackCta(MOE_PAYWALL_CTA.TOPUP, { targetPackageCode: topUpPackageCode });
    history.push(`${route.PAYMENT}?package=${topUpPackageCode}`);
  };

  const handleBuyNow = () => {
    onClose();
    const upgradePackage = handleGetPackageCodeMapping({
      level: usingPackage?.level,
      type: usingPackage?.type,
      expiresIn: usingPackage?.expiresIn,
    });
    trackCta(MOE_PAYWALL_CTA.UPGRADE, { targetPackageCode: upgradePackage });

    const redirectRoute = isCustomPackage
      ? `${route.PAYMENT}?package=${upgradePackage}&credits=${defaultCredits}&devices=${defaultDevices}&voiceClonings=${defaultVoiceClonings}&concurrentRequestApi=${concurrentRequestApi}`
      : `${route.PAYMENT}?package=${upgradePackage}`;
    history.push(redirectRoute);
  };

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      title={t('checkNotEnoughCreditsTitle')}
      styleTitle={{ textAlign: 'center' }}
      contentComponent={
        <Typography
          style={{ textAlign: 'center' }}
          variant="body-16-regular"
          color={COLOR.descriptionGray}
          dangerouslySetInnerHTML={{
            __html: showBuyMoreCredits
              ? t('descriptionBuyMoreCredits')
              : t('descriptionBuyMoreCreditsVip'),
          }}
        />
      }
      fullWidth
      image={CrownImg}
      imageContainerSize="100px"
      width="640px"
      isSingleActionButton={!showBuyMoreCredits}
      actionComponent={
        <>
          {showBuyMoreCredits && (
            <CommonButton
              variant="outlined"
              onClick={handleBuyMoreCredits}
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                ...BUTTON_40_STYLE,
                padding: '8px 16px',
              }}
            >
              {t('buyMoreCredits')}
            </CommonButton>
          )}
          <CommonButton
            variant="contained"
            onClick={handleBuyNow}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              ...BUTTON_40_STYLE,
              padding: '8px 16px',
            }}
          >
            {t('buyNow')}
          </CommonButton>
        </>
      }
    />
  );
};

export default PopupNotEnoughCredits;
